import axios from 'axios';
import { authApi } from './authService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DOCUMENT SERVICE - Handles document upload and download operations
 */

class DocumentService {
  static instance = null;

  static getInstance() {
    if (!DocumentService.instance) {
      DocumentService.instance = new DocumentService();
    }
    return DocumentService.instance;
  }

  getAuthHeaders() {
    const token = authApi.getToken();
    return {
      Authorization: `Bearer ${token}`
    };
  }

  /**
   * Upload documents to Google Drive
   */
  async uploadDocuments(files) {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (this.onProgress) {
            this.onProgress(percentCompleted);
          }
        }
      });

      return {
        success: true,
        message: response.data.message,
        uploadedFiles: response.data.uploadedFiles,
        fileCount: response.data.fileCount
      };
    } catch (error) {
      const message = error.response?.data?.detail || 'Upload failed. Please try again.';
      return { success: false, message };
    }
  }

  /**
   * Set upload progress callback
   */
  setProgressCallback(callback) {
    this.onProgress = callback;
  }

  /**
   * List uploaded documents (by client)
   */
  async listUploadedDocuments() {
    try {
      const response = await axios.get(`${API}/documents/uploads`, {
        headers: this.getAuthHeaders()
      });
      return { success: true, documents: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load documents.';
      return { success: false, message, documents: [] };
    }
  }

  /**
   * List downloadable documents (from preparer)
   */
  async listDownloadableDocuments() {
    try {
      const response = await axios.get(`${API}/documents/downloads`, {
        headers: this.getAuthHeaders()
      });
      return { success: true, documents: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load documents.';
      return { success: false, message, documents: [] };
    }
  }

  /**
   * Download a specific document
   */
  async downloadDocument(fileId, fileName) {
    try {
      const response = await axios.get(`${API}/documents/download/${fileId}`, {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Download failed.';
      return { success: false, message };
    }
  }
}

export const documentApi = DocumentService.getInstance();
